/**
 * CompanyManagementHub — iter-217c
 *
 * New hub under CLOUDGM FZ LLC (للإشراف) that groups admin-level company
 * management surfaces. Two entry points for now:
 *
 *   1) الشركات المسجلة من Cloud GM وشركات الباقات   (moved from FZLC-003)
 *   2) شهادات لمن يهمه الأمر والتهنئة              (new certificates hub)
 *
 * Route: /manage/cloudgm-fz-llc
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Briefcase, Award, FileText, FileEdit, ChevronLeft, ChevronRight } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

export default function CompanyManagementHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const Arrow = isAr ? ChevronLeft : ChevronRight;

  const CARDS = [
    {
      id: 'agent',
      code: 'FZLC-003',
      titleAr: 'الشركات المسجلة من Cloud GM وشركات الباقات',
      titleEn: 'Registered Cloud GM Companies & Package Companies',
      descAr:  'إدارة الشركات المسجلة من CloudGM والباقات المرتبطة بها',
      descEn:  'Manage companies registered via CloudGM and their linked packages',
      icon:    Briefcase,
      color:   '#10b981',
      route:   '/manage/cloudgm-agent',
    },
    {
      id: 'certificates',
      code: 'FZLC-004',
      titleAr: 'شهادات لمن يهمه الأمر والتهنئة',
      titleEn: 'To-Whom-It-May-Concern & Congratulations Certificates',
      descAr:  'إصدار شهادات رسمية على ورقة CloudGM (تجار، عملاء، شركة، موظفين)',
      descEn:  'Issue formal certificates on CloudGM letterhead (merchants, customers, company, employees)',
      icon:    Award,
      color:   '#F59E0B',
      route:   '/manage/cloudgm-fz-llc/certificates',
    },
    {
      id: 'quotations',
      code: 'FZLC-005',
      titleAr: 'عروض الأسعار',
      titleEn: 'Price Quotations',
      descAr:  'إنشاء ومتابعة عروض أسعار بالتنسيق الرسمي (QT-2026-00258)',
      descEn:  'Create and track price quotations in CloudGM official format (QT-2026-00258)',
      icon:    FileText,
      color:   '#3B9EDC',
      route:   '/manage/cloudgm-fz-llc/quotations',
    },
    {
      id: 'blank-form',
      code: 'FZLC-006',
      titleAr: 'فورم الشركة',
      titleEn: 'Company Blank Form',
      descAr:  'ورق رسمي فارغ متعدد الصفحات بهوية CloudGM — يُطبع أو يُحمَّل PDF (بدون حفظ)',
      descEn:  'Blank multi-page CloudGM letterhead — print or download PDF (nothing is saved)',
      icon:    FileEdit,
      color:   '#A78BFA',
      route:   '/manage/cloudgm-fz-llc/blank-form',
    },
  ];

  return (
    <DashboardLayout
      title="إدارة شركة CLOUDGM FZ LLC"
      titleEn="CLOUDGM FZ LLC Management"
      subtitle="مركز إدارة الشركة — الشركات المسجلة والشهادات الرسمية"
      subtitleEn="Company management hub — registered companies and formal certificates"
      screenCode="FZLC-MGMT"
      backPath="/dashboard"
      onBack={() => navigate('/dashboard')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'CLOUDGM FZ LLC (للإشراف)', labelEn: 'CLOUDGM FZ LLC (Supervision)', path: '/dashboard' },
        { labelAr: 'إدارة شركة CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC Management' },
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="company-management-hub">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {visiblePages(CARDS, 'route').map((c, i) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => navigate(c.route)}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border transition-all text-start"
              style={{ borderColor: `${c.color}30` }}
              data-testid={`company-mgmt-card-${c.id}`}
            >
              <div className="absolute top-3 start-3 text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-white/40 border border-white/5">
                {c.code}
              </div>
              <div className="flex items-start gap-4 mt-3">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 shrink-0"
                  style={{ backgroundColor: `${c.color}15`, borderColor: `${c.color}40` }}
                >
                  <c.icon className="h-7 w-7" style={{ color: c.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                    {isAr ? c.titleAr : c.titleEn}
                    <Arrow className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: c.color }} />
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {isAr ? c.descAr : c.descEn}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}
